<template>
  <div
    v-if="!subscriptionIsFreeTrial && $store.state.idea.storyMode.common.completedSteps.financingCompleted === true && isFinancingVisible"
    class="journal-content dynamic-height"
    :class="{'dynamic-height': !isOption(financingData, 'breakdownSection') && !isOption(financingData, 'timelineSection') && !isOption(financingData, 'tableSection')}"
  >
    <div class="content-menus">
      <div v-if="moreVisible === true" class="view-type">
        <template v-if="financing.length !== 0">
          <a
            class="orange"
            :class="{'orange-off': !isOption(financingData, 'breakdownSection')}"
            @click="enableBreakdown"
          >
            {{ $t('pages.businessPlan.sections.breakdown') }}
          </a>
          <a
            class="orange"
            :class="{'orange-off': !isOption(financingData, 'timelineSection')}"
            @click="enableTimeline"
          >
            {{ $t('pages.businessPlan.sections.timeline') }}
          </a>
          <a
            class="orange"
            :class="{'orange-off': !isOption(financingData, 'tableSection')}"
            @click="enableTable"
          >
            {{ $t('pages.businessPlan.sections.table') }}
          </a>
        </template>
        <p v-if="financing.length === 0">
          {{ $t('pages.businessPlan.sections.youDontHaveAnyDataOnThisPage') }}
        </p>
      </div>
      <div v-if="canEdit" class="mini-actions">
        <toggle-page-items-button
          :visible="moreVisible"
          @click="toggleMore"
        />
        <toggle-view-button
          :disabled="!cardExist"
          :horizontal="!isOption(financingData, 'gridListView')"
          @click="toggleRotate"
        />
        <toggle-notes-button
          :disabled="!moreDetails"
          :visible="isOption(financingData, 'moreDetailsDisplay')"
          @click="enableMoreDetails"
        />
        <edit-section-link route-name="business-guide-set-up-financing" />
      </div>
    </div>

    <div class="sub chapter">
      <header>
        <!-- Title section -->
        <editable-title
          v-model="financingData.title"
          tag="h1"
          :disabled="!canEdit || shareMode"
          @save="onSave"
        />
        <!-- Title section -->
        <hr>
        <!-- Description section -->
        <editable-description
          v-model="financingData.subTitle"
          :empty-text="$t('addMoreDetails.addNoteText')"
          :disabled="!canEdit || shareMode"
          @save="onSave"
        />
        <!-- Description section -->
      </header>

      <template v-if="financing.length !== 0">
        <!-- Breakdown chart -->
        <div v-if="isOption(financingData, 'breakdownSection')" class="switcher-inner-wrapper breakdown-chart">
          <financing-breakdown
            :switch-selector="switchSelector"
            :chart-adjustment="chartAdjustment"
            :update-chart="updateCharts"
          />
        </div>
        <!-- /Breakdown chart -->

        <!-- Timeline chart -->
        <div v-if="isOption(financingData, 'timelineSection')" class="switcher-inner-wrapper timeline-chart">
          <financing-timeline
            :switch-selector="switchSelector"
            :chart-adjustment="chartAdjustment"
            :update-chart="updateCharts"
          />
        </div>
        <!-- /Timeline chart -->

        <!-- Finance Table -->
        <div v-if="isOption(financingData, 'tableSection')" class="switcher-inner-wrapper table-chart">
          <financing-table
            :table-adjustment="tableAdjustment"
            :update-chart="updateCharts"
          />
        </div>
        <!-- /Finance Table -->
      </template>

      <!-- More Details section -->
      <template v-if="moreDetails && isOption(financingData, 'moreDetailsDisplay')">
        <div class="details">
          <editable-title
            v-model="financingData.moreDetailsTitle"
            class="bold"
            tag="p"
            :disabled="!canEdit"
            @save="onSave"
          />
          <p v-html="moreDetails" />
        </div>
      </template>
      <!-- More Details section -->
    </div>
  </div>
</template>

<script>
import EditSectionLink from '@/views/Home/StoryMode/Components/BusinessPlan/EditSectionLink'
import EditableDescription from '@/components/_v2/EditableDescription'
import EditableTitle from '@/components/_v2/EditableTitle'
import FinancingBreakdown from '@/views/Home/StoryMode/SetUp/Financing/Charts/FinancingBreakdown'
import FinancingTable from '@/views/Home/StoryMode/SetUp/Financing/Tables/FinancingTable'
import FinancingTimeline from '@/views/Home/StoryMode/SetUp/Financing/Charts/FinancingTimeline'
import MixinIdeaRoles from '@/mixins/permissions/ideaRoles'
import MixinUserSubscription from '@/mixins/permissions/userSubscription'
import ToggleNotesButton from '@/views/Home/StoryMode/Components/BusinessPlan/ToggleNotesButton'
import TogglePageItemsButton from '@/views/Home/StoryMode/Components/BusinessPlan/TogglePageItemsButton'
import ToggleViewButton from '@/views/Home/StoryMode/Components/BusinessPlan/ToggleViewButton'
import journalMethods from '@/mixins/journalMethods'

export default {
  name: 'Financing',

  components: {
    EditSectionLink,
    EditableDescription,
    EditableTitle,
    FinancingBreakdown,
    FinancingTable,
    FinancingTimeline,
    ToggleNotesButton,
    TogglePageItemsButton,
    ToggleViewButton
  },

  mixins: [
    journalMethods,
    MixinIdeaRoles,
    MixinUserSubscription
  ],

  props: {
    allPagesTextData: {
      type: Object,
      default: () => ({})
    },
    shareMode: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      cardExist: false,
      financingData: {},
      breakdownSection: true,
      timelineSection: true,
      tableSection: true,

      moreVisible: false,
      updateCharts: true,

      moreDetails: this.$store.state.idea.storyMode.common.moreDetails.financingMoreDetails,
      financing: this.$store.state.idea.storyMode.setUp.financing.finances,

      chartAdjustment: {
        chartHeight: 260,
        headingTitleFontSize: '18px',
        xLabelTitleFontSize: '10px',
        yLabelTitleFontSize: '12px',
        yaxisLabelsMinWidth: {
          breakDown: 0,
          timeLine: 0
        }
      },
      tableAdjustment: {
        journalPage: true,
        period: 'yearly',
        tableFirstColumnWidth: 250,
        columnWidth: 118
      },
      switchSelector: 'financing'
    }
  },

  computed: {
    isFinancingVisible () {
      const index = this.$store.state.idea.journal.journalSwitchBars.indexOf(
        this.$store.state.idea.journal.journalSwitchBars.find(journalElement => journalElement.sectionName === 'Financing')
      )

      return !this.$store.state.idea.journal.journalSwitchBars[index].options.includes('switch')
    },

    isTitleEnabled () {
      return this.financingData.title
    },

    isMoreDetailsTitleEnabled () {
      return this.financingData.moreDetailsTitle
    }
  },

  watch: {
    allPagesTextData () {
      this.financingData = Object.assign({}, this.allPagesTextData)
    }
  },

  mounted () {
    this.financingData = Object.assign({}, this.allPagesTextData)
  },

  methods: {
    toggleRotate () {
      if (this.cardExist) {
        this.toggleOption(this.financingData, 'gridListView')
        this.financingData = Object.assign({}, this.allPagesTextData)
        this.onSave()
      }
    },

    enableMoreDetails () {
      if (this.moreDetails) {
        this.toggleOption(this.financingData, 'moreDetailsDisplay')
        this.financingData = Object.assign({}, this.allPagesTextData)
        this.onSave()
      }
    },

    onSave () {
      this.$emit('save', this.financingData)
    },

    enableTimeline () {
      this.toggleOption(this.financingData, 'timelineSection')
      this.financingData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    },

    enableBreakdown () {
      this.toggleOption(this.financingData, 'breakdownSection')
      this.financingData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    },

    enableTable () {
      this.toggleOption(this.financingData, 'tableSection')
      this.financingData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    }
  }
}
</script>
